import Tippy from '@tippyjs/react';
import saveAs from 'file-saver';
import { t } from 'i18next';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ReactComponent as FileDoc } from '../../../assets/svg/file-doc.svg';
import { ReactComponent as Filter } from '../../../assets/svg/filter.svg';
import { useRole } from '../../../hooks';
import { ReactComponent as StatusBad } from '../../../img/icons/order-status-bad.svg';
import { ReactComponent as StatusNa, ReactComponent as Unknown } from '../../../img/icons/order-status-na.svg';
import { ReactComponent as Ok2 } from '../../../img/icons/order-status-ok-2.svg';
import { ReactComponent as Ok, ReactComponent as StatusOk } from '../../../img/icons/order-status-ok.svg';
import { ReactComponent as StatusWaitGray } from '../../../img/icons/order-status-wait-grey.svg';
import { ReactComponent as StatusWait } from '../../../img/icons/order-status-wait.svg';
import { ReactComponent as Crit } from '../../../img/icons/order-status-warning-red.svg';
import { ReactComponent as Warn } from '../../../img/icons/order-status-warning.svg';
import { useGetXlsMutation } from '../../../services/api';
import { AccessPermissionStatus, ContractStatus, CrmStatus, PortfolioRecord, PortfolioRecordDetailContract, PortfolioRecordDetailPermission, useLazyGetPortfolioAccountsQuery, useLazyGetPortfolioDetailQuery, useLazyGetPortfolioQuery } from '../../../services/content_v1';
import { OuterMenuProps } from '../../../types/index';
import { isMedialogistika, parseDate, toMplatDate, toMplatValidity } from '../../common';
import Button from '../Button/Button';
import Table from '../DataTable/Table';
import TableBody from '../DataTable/TableBody';
import TableCollapsibleArrow from '../DataTable/TableCollapsibleArrow';
import { TableColumn } from '../DataTable/TableColumn';
import TableHeader from '../DataTable/TableHeader';
import { TableHeaderColumn } from '../DataTable/TableHeaderColumn';
import TableRow from '../DataTable/TableRow';
import ImageLoader from '../ImageLoader';
import MainContentFilter from '../MainContentFilter';
import MobileFilter from '../MobileFilter';
import { MobileFilterItem } from '../MobileFilterItem';
import ContractModal from '../Modals/ContractModal';
import { PermissionViewModal } from '../Modals/PermissionViewModal';
import MultiSelect from '../MultiSelect';
import Pagination from '../Pagination';
import { Searcher } from '../Searcher';


export const getContractStatus = (s: ContractStatus) => {
    switch (s) {
        case ContractStatus.CONCLUDED:
            return <StatusOk />
        case ContractStatus.AGREEMENT:
            return <StatusWait />
        case ContractStatus.TERMINATED:
            return <StatusBad />;
        default:
            return <StatusNa />
    }
}

export const getContractStatusText = (c: PortfolioRecordDetailContract | undefined) => {
    const s = c?.status;
    const now = new Date();
    const upper = c?.validity?.upper && parseDate(c.validity.upper);
    switch (s) {
        case ContractStatus.CONCLUDED:
            if (!c?.validity?.lower && !upper) {
                return <Trans>Validity period not specified</Trans>
            }
            if (upper && upper < now) {
                return <Trans>EXPIRED</Trans>
            }
            if (upper) {
                const dt = (upper as Date).getTime() - now.getTime();
                const maxDt = 30 * 24 * 60 * 60 * 1000;
                if (dt < maxDt) {
                    return <><Trans>order:CONTRACT_CONCLUDED</Trans>. <Trans>EXPIRES</Trans> {toMplatDate(upper.toString(), true)}</>
                }
            }
            return <Trans>order:CONTRACT_CONCLUDED</Trans>
        case ContractStatus.AGREEMENT:
            return <Trans>order:CONTRACT_AGREEMENT</Trans>
        case ContractStatus.TERMINATED:
            return <Trans>order:CONTRACT_TERMINATED</Trans>
        default:
            return <Trans>Unknown</Trans>
    }
}

export function toPermissionStatus(c?: AccessPermissionStatus) {
    switch (c) {
        case AccessPermissionStatus.ISSUED:
            return <StatusOk />
        case AccessPermissionStatus.WORKING:
            return <StatusWaitGray />
        case AccessPermissionStatus.REVOKED:
            return <StatusBad />
        default:
            return <StatusNa />
    }
}

export function toPermissionStatusText(c?: PortfolioRecordDetailPermission) {
    const s = c?.status;
    const now = new Date();
    const upper = c?.validity.upper && parseDate(c.validity.upper);
    switch (s) {
        case AccessPermissionStatus.ISSUED:
            if (!c?.validity.lower && !upper) {
                return <Trans>Validity period not specified</Trans>
            }
            if (upper && upper < now) {
                return <Trans>EXPIRED</Trans>
            }
            if (upper) {
                const dt = (upper as Date).getTime() - now.getTime();
                const maxDt = 30 * 24 * 60 * 60 * 1000;
                if (dt < maxDt) {
                    return <><Trans>ISSUED</Trans>. <Trans>EXPIRES</Trans> {toMplatDate(upper.toString(), true)}</>
                }
            }
            return <Trans>ISSUED</Trans>
        case AccessPermissionStatus.WORKING:
            return <Trans>WORKING</Trans>
        case AccessPermissionStatus.REVOKED:
            return <Trans>REVOKED</Trans>
        default:
            return <Trans>Unknown</Trans>
    }
}

export const getCrmStatus = (s: CrmStatus | null | undefined) => {
    switch (s) {
        case CrmStatus.OK:
            return <span className="portfolio-list__subs-status">
                <Ok />
            </span>
        case CrmStatus.WARN:
            return <Link to={''} className="portfolio-list__subs-status">
                <Warn />
            </Link>
        case CrmStatus.CRIT:
            return <Link to={''} className="portfolio-list__subs-status">
                <Crit />
            </Link>
        case CrmStatus.PROLONGED:
            return <Link to={''} className="portfolio-list__subs-status">
                <Ok2 />
            </Link>
        default:
            return <span className="portfolio-list__subs-status">
                <Unknown />
            </span>;
    }
};

interface MatrixTableRowProps {
    unit: PortfolioRecord
}

function MatrixTableRow({ unit }: MatrixTableRowProps) {
    const [fetchPortfolioDetail, { data }] = useLazyGetPortfolioDetailQuery();

    const [active, setActive] = useState(false);
    const [h, setH] = useState(0);
    const divRef = useRef<HTMLDivElement | null>(null);
    const [showContract, setShowContract] = useState(false);
    const [contractId, setContractId] = useState(NaN);
    const [showPermissionViewModal, setShowPermissionViewModal] = useState(NaN);

    useEffect(() => {
        if (divRef.current) {
            setH(divRef.current.clientHeight);
        }
    }, [divRef.current, active, data?.results]);

    useEffect(() => {
        if (active && unit.unit && unit.provider && unit.consumer) {
            fetchPortfolioDetail({ offset: 0, limit: 0, consumer_id: unit.consumer.id, provider_id: unit.provider.id, unit_id: unit.unit.id });
        }
    }, [active]);

    return (<>
        <ContractModal isOpen={showContract} contractId={contractId} onClose={() => setShowContract(false)} />
        <PermissionViewModal isOpen={!isNaN(showPermissionViewModal)} permId={showPermissionViewModal} onClose={() => setShowPermissionViewModal(NaN)} />

        <TableRow className={active ? '_active' : undefined} onClick={() => setActive((v) => !v)}>
            <TableColumn className="_id">{unit.unit ? `TV${unit.unit.id}` : 'Контент удален'}</TableColumn>
            <TableColumn className="_content">
                <div className="permissions-list__content-channels-item">
                    <ImageLoader url={unit.unit?.covers.at(0) || ''} alt="" className="permissions-list__content-channels-item-image" />
                    <div className="permissions-list__content-channels-item-body">
                        <div className="permissions-list__content-channels-item-name">{unit.unit?.title}</div>
                    </div>
                </div>
            </TableColumn>
            <TableColumn className="_subscriber">
                <Link className="link-accent" to={`/accounts/view/${unit.consumer?.id}`}>{unit.consumer?.name}</Link> · <span className="nobr">{unit.consumer?.org?.name}</span>
            </TableColumn>
            <TableColumn className="_source">
                {unit.sources.map((s, i) => {
                    return isMedialogistika(s.name) ? <Link className="link-accent" to={'/medialogistika'}>{s.name}{i === unit.sources.length - 1 ? '' : ', '}</Link> : <span key={s.id}>
                        {s.name}
                        {i === unit.sources.length - 1 ? '' : ', '}
                    </span>
                })}
            </TableColumn>
            <TableColumn className="_status">
                <span className="icon__status">
                    {getCrmStatus(unit.crm_status)}
                </span>
            </TableColumn>
            <TableColumn className="_collapsible-arrow">
                <TableCollapsibleArrow />
            </TableColumn>
        </TableRow>
        {active && (
            <div className="table-list__collapsible _active" style={{ display: 'block', height: `${h}px` }}>
                <div className="table-list__collapsible-body">
                    <div ref={divRef}>
                        {data?.results.data.map((detail, i) => <>
                            <div key={i} className="portfolio-list__item">
                                <div className="portfolio-list__item-col">
                                    <div className="portfolio-list__item-heading">
                                        {detail?.contracts?.at(0)?.id ?
                                            <Link to="#" className="link-accent" onClick={(e) => {
                                                e.preventDefault();
                                                setContractId(detail.contracts.at(0)?.id || NaN);
                                                setShowContract(true);
                                            }}><Trans>Contract</Trans></Link>
                                            :
                                            <Trans>Contract</Trans>}
                                    </div>
                                    <div className="portfolio-list__item-list">
                                        <div className="portfolio-list__item-param">
                                            <div className="portfolio-list__item-param-name"><Trans>Number</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Date</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Status</Trans></div>
                                        </div>
                                        <div className="portfolio-list__item-param">
                                            <div className="portfolio-list__item-param-value">{detail?.contracts?.at(0)?.number || '-'}</div>
                                            <div className="portfolio-list__item-param-value">{detail.contracts?.at(0)?.concluded ? toMplatDate(detail.contracts?.at(0)?.concluded, true) : '-'}</div>
                                            <div className="portfolio-list__item-param-value">
                                                <Tippy
                                                    arrow={false}
                                                    content={getContractStatusText(detail.contracts?.at(0))}
                                                    placement="right-start"
                                                    animation="shift-away"
                                                    theme="light"
                                                    duration={[100, 100]}
                                                >
                                                    <span className="icon__status">
                                                        {getContractStatus(detail.contracts?.at(0)?.status || 0)}
                                                    </span>
                                                </Tippy>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="portfolio-list__item-col">
                                    <div className="portfolio-list__item-heading">
                                        {detail?.accesspermission ?
                                            <Link to="#" className="link-accent" onClick={(e) => {
                                                e.preventDefault();
                                                setShowPermissionViewModal(detail.accesspermission.id);
                                            }}><Trans>Permission</Trans></Link>
                                            :
                                            <Trans>Permission</Trans>}
                                    </div>
                                    <div className="portfolio-list__item-list">
                                        <div className="portfolio-list__item-param">
                                            <div className="portfolio-list__item-param-name"><Trans>Number</Trans></div>
                                            {/* <div className="portfolio-list__item-param-name"><Trans>Date</Trans></div> */}
                                            <div className="portfolio-list__item-param-name"><Trans>Term</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Parameters</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Status</Trans></div>
                                        </div>
                                        <div className="portfolio-list__item-param">
                                            <div className="portfolio-list__item-param-value">{detail.accesspermission?.id || '-'}</div>
                                            {/* <div className="portfolio-list__item-param-value">
                                                {detail.accesspermission?.issued ? toMplatDate(detail.accesspermission?.issued, true) : '-'}
                                            </div> */}
                                            <div className="portfolio-list__item-param-value">{toMplatValidity(detail.accesspermission?.validity)}</div>
                                            <div className="portfolio-list__item-param-value">{detail.accesspermission?.params.fmt.map((f) => f.name).join(', ')} - {detail.accesspermission?.params.version}</div>
                                            <div className="portfolio-list__item-param-value">
                                                <Tippy
                                                    arrow={false}
                                                    content={toPermissionStatusText(detail.accesspermission)}
                                                    placement="right-start"
                                                    animation="shift-away"
                                                    theme="light"
                                                    duration={[100, 100]}
                                                >
                                                    <span className="icon__status">
                                                        {getCrmStatus(detail.accesspermission?.crm_status)}
                                                    </span>
                                                </Tippy>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="portfolio-list__item-col">
                                    <div className="portfolio-list__item-heading"><Trans>Access</Trans></div>
                                    <div className="portfolio-list__item-list">
                                        <div className="portfolio-list__item-param">
                                            <div className="portfolio-list__item-param-name"><Trans>TV signal</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Source</Trans></div>
                                            <div className="portfolio-list__item-param-name"><Trans>Status</Trans></div>
                                        </div>
                                        {detail.signals.map((s) =>
                                            <div className="portfolio-list__item-param">
                                                <div className="portfolio-list__item-param-value">{s.name}</div>
                                                <div className="portfolio-list__item-param-value">
                                                    {isMedialogistika(s.source.name) ? <Link className="link-accent" to={'/medialogistika'}>{s.source.name}</Link> : s.source.name}
                                                </div>
                                                <div className="portfolio-list__item-param-value">
                                                    <span className="icon__status">
                                                        {getCrmStatus(s.crm_status)}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>)}
                    </div>
                </div>
            </div>
        )}
    </>);
}

const PAGE_SIZE = 20;

function DealPortfolioProviderMatrix({ menu }: OuterMenuProps) {
    const { superAdmin } = useRole();

    const [fetchAccounts, { data: accData }] = useLazyGetPortfolioAccountsQuery();
    const [fetchPortfolio, { data: pData }] = useLazyGetPortfolioQuery();
    const [trigger, { data: xlsData }] = useGetXlsMutation();

    const [ordering, setOrdering] = useState('unit');
    const [current, setCurrent] = useState(0);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState<string>('');
    const [records, setRecords] = useState<PortfolioRecord[]>([]);

    // const [promo, setPromo] = useState(false);
    const [accFilter, setAccFilter] = useState<number[]>([]);
    const [typeFilter, setTypeFilter] = useState<number[]>([]);
    const [tStyle, setTStyle] = useState<React.CSSProperties>({ '--width': '1300px' } as unknown as React.CSSProperties);

    const deferredSearch = useDeferredValue(search);

    const accList = useMemo(() => {
        return accData?.results.map((u) => ({ value: u.id, label: u.name }));
    }, [accData]);

    const availTypes = useMemo(() => {
        return [
            { value: CrmStatus.OK, label: 'Ok' },
            { value: CrmStatus.WARN, label: t('Warning') },
            { value: CrmStatus.CRIT, label: t('Problem') },
        ];
    }, []);

    const [filterVisible, setFilterVisible] = useState(false);
    const [filterCount, setFilterCount] = useState<number>(0);

    useEffect(() => {
        if (pData?.results.data) {
            setTotal(pData.results.count);
            setRecords(pData.results.data);
        }
    }, [pData]);

    useEffect(() => {
        setFilterCount(
            accFilter.length
            + typeFilter.length
        )
    }, [accFilter, typeFilter]);

    const onClean = useCallback(() => {
        setSearch('');
        setAccFilter([]);
        setTypeFilter([]);
    }, [accFilter, typeFilter, search]);

    useEffect(() => {
        if (xlsData) {
            saveAs(xlsData, 'portfolio-matrix.xls');
        }
    }, [xlsData]);

    useEffect(() => {
        fetchAccounts();
    }, []);

    useEffect(() => {
        fetchPortfolio({ offset: current, limit: PAGE_SIZE, ordering, search: deferredSearch, consumer: accFilter.join(','), crm_status: typeFilter.join(',') });
    }, [accFilter, typeFilter, current, deferredSearch, ordering])

    return (
        <>
            <MainContentFilter>
                {menu}

                <div className="main__controls _no-sort">
                    <div className="main__controls-filter">
                        <Searcher value={search} onChange={(v) => setSearch(v)} placeholder={t('Search by registry')} />

                        <MultiSelect className="main__controls-filter-item _mid" placeholder={t('Consumer')}
                            value={accFilter} options={accList || []} setValue={(v) => setAccFilter(v)} minimized />
                        <MultiSelect className="main__controls-filter-item _mid _pre-checkbox" placeholder={t('Status')}
                            value={typeFilter} options={availTypes} setValue={(v) => setTypeFilter(v)} minimized />
                        {/* <CheckBox value={promo} onChange={(v) => setPromo(v)} label={t('Promotions')} /> */}
                        {(filterCount > 0 || search) && <div className="main__controls-filter-item">
                            <Button className="_bd" size="small" title={t('Clean')} onClick={onClean} style={{ lineHeight: '24px' }}>
                                🧹
                            </Button>
                        </div>}
                        <div className="main__controls-filter-trigger" onClick={() => setFilterVisible(!filterVisible)}>
                            <Filter />
                            {!!filterCount && <span className="main__controls-filter-trigger-counter">{filterCount}</span>}
                        </div>
                        <a href="#xls" className="main__types-file" style={{ display: superAdmin ? undefined : 'none' }} target="_blank" onClick={(e) => {
                            e.preventDefault();
                            const params: Record<string, string> = { ordering, search: deferredSearch, consumer: accFilter.join(','), crm_status: typeFilter.join(',') };
                            const urlParams = Object.keys(params).filter(k => params[k]).map(k => params[k].toString().split(',').map(mk => k + '=' + mk).join('&')).join('&');
                            trigger(`/be/api/v1/deals/crm/portfolio/xlsx/?${urlParams}`);
                        }}>
                            <FileDoc />
                            <Trans>Orders xls export</Trans>
                        </a>
                    </div>
                </div>
            </MainContentFilter>
            <Table className="_collapsible portfolio-list" style={tStyle} onResize={(w) => setTStyle({ '--width': `${w}px` } as unknown as React.CSSProperties)}>
                <TableHeader>
                    <TableHeaderColumn header={'ID'} field="unit" className="_datetime" ordering={ordering} setOrdering={setOrdering} />
                    <TableHeaderColumn header={t('TV (content)')} className="_content" />
                    <TableHeaderColumn header={t('Consumer')} className="_subscriber" />
                    <TableHeaderColumn header={t('Source')} className="_source" />
                    <TableHeaderColumn header={t('Status')} className="_status" />
                    <TableHeaderColumn header={''} className="_arrow" />
                </TableHeader>
                <TableBody>
                    {records.map((unit, i) => {
                        return <MatrixTableRow key={i} unit={unit} />
                    })}
                </TableBody>
            </Table>
            {total > PAGE_SIZE && <div className="table-list__more">
                <Pagination
                    first={current}
                    rows={PAGE_SIZE}
                    totalRecords={total}
                    onPageChange={(e) => { setCurrent(e.first); }}
                />
            </div>}
            <MobileFilter
                filterVisible={filterVisible}
                setFilterVisible={setFilterVisible}
                onClean={onClean}
            >
                <MobileFilterItem label={t('Consumer')} options={accList || []} value={accFilter} setValue={setAccFilter} />
                <MobileFilterItem
                    label={t('Type')}
                    value={typeFilter}
                    options={availTypes}
                    setValue={setTypeFilter} />
            </MobileFilter>
        </>
    );
}

export default DealPortfolioProviderMatrix;
